import { useState, useMemo } from "react";
import { useScores } from "~/sdks/LocalContent/features/useScores";
import { scoreCategories } from "@local-logic/elements-react";
import { useFlags } from "launchdarkly-react-client-sdk";

const useRankedCategories = () => {
  const flags = useFlags();
  const [filteredScores] = useState(
    flags.lcWellnessTab
      ? scoreCategories
      : scoreCategories.filter((score) => score.value !== "wellness")
  );
  const { data, isLoading, isFetching } = useScores();

  const sortedCategories = useMemo(() => {
    if (isLoading || isFetching || typeof data === "undefined") {
      return [];
    }

    const isScoreEmpty = (value: string) =>
      Number.parseFloat(value).toFixed(1) === "0.0";
    const dataKeys = Object.keys(data.location);
    const rankedTabs: number[] = [];

    filteredScores.forEach((category) => {
      let rank = 0;

      category.scores.forEach((score) => {
        const scoreValue = Number(
          Number.parseFloat(data.location[score]?.value).toFixed(1)
        );

        // #1 For each presented/visible score, +1 to category score
        //  #1a Exclude from count scores with zero value or not-available value
        if (
          dataKeys.includes(score) &&
          !isScoreEmpty(data.location[score]?.value)
        ) {
          rank += 1;
        }

        // #2 For each presented/visible score with a value above 5, +1 OR
        // #3 Anything above 8, +2 to the category score
        // NOTE: Scores are out of 5. Not out of 10 (we just display out of 10)
        if (scoreValue >= 4) {
          rank += 2;
        } else if (scoreValue >= 2.5) {
          rank += 1;
        }
      });

      rankedTabs.push(rank);
    });

    const sorted = filteredScores
      .map((category, index) => ({
        ...category,
        rank: rankedTabs[index],
      }))
      .sort((a, b) => b.rank - a.rank)
      .map((category) => ({
        value: category.value,
        icon: category.icon,
        scores: category.scores,
      }));

    return sorted;
  }, [data, isLoading, isFetching]);

  return { data: sortedCategories, isLoading, isFetching };
};

export default useRankedCategories;
